import { IS_ZONE_OF_RELEVANCE_ENABLED, IS_LOYALTY_FALLBACK, GET_BRAND, GET_ZOR_CARDS, GET_ZOR_TABS } from '@/types/getters';
import { SET_RVI_IDS, SET_ZOR_INFO, SET_ZOR_CARDS, SET_ZOR_TABS, SET_IS_LOYALTY_FALLBACK, SET_PROS_ZONES_INFO } from '@/types/mutations';
import { FETCH_ZOR_CARDS, FETCH_ZOR_TABS } from '@/types/actions';
import { isEmpty, userInfo } from '@/utils';
import { getRviCard } from './helpers/getRviCard';
import { getProsCards } from './helpers/getProsCards';
import { getProsTabs } from './helpers/tabs/getProsTabs';
import { MAX_TABS_COUNT, RVI_TAB_TITLE } from './constants/tabs';
import { MAX_CARDS_COUNT } from './constants/constants';
import { defineStore } from 'pinia';
import { useEnvProps } from '@/stores/envProps';

export const useZoneOfRelevance = defineStore('zoneOfRelevance', {
  state: () => ({
    rviIds: [],
    rviProduct: {},
    zorInfo: {},
    isZoneOfRelevanceEnabled: false,
    prosZonesInfo: {},
    zorCards: [],
    zorTabs: [],
    isLoyaltyFallback: false,
  }),
  getters: {
    [GET_BRAND]() {
      return import.meta.env.VITE_ENTRY_POINT;
    },
    [IS_ZONE_OF_RELEVANCE_ENABLED](state) {
      return state.isZoneOfRelevanceEnabled;
    },
    [IS_LOYALTY_FALLBACK](state) {
      return state.isLoyaltyFallback;
    },
    [GET_ZOR_CARDS](state) {
      return state.zorCards;
    },
    [GET_ZOR_TABS](state) {
      return state.zorTabs;
    },
  },
  actions: {
    [SET_RVI_IDS](payload) {
      this.rviIds = payload;
    },
    [SET_ZOR_INFO](canvasObj) {
      if (isEmpty(canvasObj)) {
        return;
      }

      this.isZoneOfRelevanceEnabled = canvasObj.meta.properties.enableZoneOfRelevance;

      if (!this.isZoneOfRelevanceEnabled) {
        return;
      }

      const zorRow = canvasObj.rows.find((row) => row?.row140?.attributes?.isZorDashboard);

      if (!zorRow) {
        return;
      }

      const prosZonesInfo = {};
      const prosZones = zorRow.row140.zones[0].carousel.slides.filter((elem) => elem.prosProductPoolMedia).map((elem) => elem.prosProductPoolMedia);
      prosZones.forEach((prosZone) => {
        prosZonesInfo[prosZone.zoneName.toLowerCase()] = {
          context: prosZone.context,
          maxRecs: prosZone.maxRecs,
        };
      });

      this.prosZonesInfo = prosZonesInfo;
    },
    [SET_PROS_ZONES_INFO](payload) {
      this.prosZonesInfo = payload;
    },
    [SET_ZOR_CARDS](payload) {
      this.zorCards = payload;
    },
    [SET_ZOR_TABS](payload) {
      this.zorTabs = payload;
    },
    [SET_IS_LOYALTY_FALLBACK](payload) {
      this.isLoyaltyFallback = payload;
    },
    async [FETCH_ZOR_CARDS]() {
      const isSignedIn = userInfo.isSignedIn();

      const envPropsStore = useEnvProps();
      const deviceType = envPropsStore.envProps?.headers?.deviceType;

      const prosCards = await getProsCards({
        prosZonesInfo: this.prosZonesInfo,
        brand: this[GET_BRAND],
        isSignedIn,
        deviceType,
      });
      if (isEmpty(prosCards)) {
        this[SET_IS_LOYALTY_FALLBACK](true);
        return;
      }

      const rviCard = await getRviCard({
        rviIds: this.rviIds,
        count: 1,
        isSignedIn,
        brand: this[GET_BRAND],
      });

      const loyaltyPlaceholderCards = deviceType === 'DESKTOP' ? [{ cardType: 'loyalty' }, { cardType: 'loyalty' }] : [];

      const zorCards = [...loyaltyPlaceholderCards, ...[...(!isEmpty(rviCard) ? [rviCard] : []), ...prosCards].slice(0, MAX_CARDS_COUNT)];
      this[SET_ZOR_CARDS](zorCards);
    },
    async [FETCH_ZOR_TABS]() {
      const userStatus = userInfo.getUserStatus();

      const envPropsStore = useEnvProps();
      const deviceType = envPropsStore.envProps?.headers?.deviceType;

      const rviTab = this.rviIds.length
        ? {
            zone: 'rvi_hp_zone',
            panelType: 'rvi',
            tabTitle: RVI_TAB_TITLE,
            componentType: 'RviCarousel',
          }
        : {};

      const prosTabs = await getProsTabs({
        prosZonesInfo: this.prosZonesInfo,
        brand: this[GET_BRAND],
        userStatus,
        deviceType,
      });

      const zorTabs = [...[...(!isEmpty(rviTab) ? [rviTab] : []), ...prosTabs].slice(0, MAX_TABS_COUNT)];
      this[SET_ZOR_TABS](zorTabs);
    },
  },
});
